<template>
  <div class="flex flex-col min-h-screen">

    <main class="flex-1">
      <div v-if="post" class="bg-white dark:bg-graphite">
        <!-- Header del post -->
        <div class="relative">
          <img
            :src="post.image"
            :alt="post.title"
            class="w-full h-64 md:h-80 object-cover"
          />
          <div class="absolute inset-0 bg-black bg-opacity-40 flex items-end">
            <div class="p-6 text-white">
              <span 
                :class="getCategoryBadgeClass(post.category)"
                class="inline-block px-3 py-1 rounded-full text-sm font-medium mb-4"
              >
                {{ getCategoryLabel(post.category) }}
              </span>
              <h1 class="text-3xl md:text-4xl font-heading font-bold mb-2">{{ post.title }}</h1>
              <div class="flex items-center gap-4 text-sm">
                <div class="flex items-center gap-1">
                  <CalendarIcon class="h-4 w-4" />
                  <span>{{ formatDate(post.date) }}</span>
                </div>
                <div v-if="post.distance" class="flex items-center gap-1">
                  <RouteIcon class="h-4 w-4" />
                  <span>{{ post.distance }}</span>
                </div>
                <div v-if="post.duration" class="flex items-center gap-1">
                  <ClockIcon class="h-4 w-4" />
                  <span>{{ post.duration }}</span>
                </div>
                <div v-if="post.location" class="flex items-center gap-1">
                  <MapPinIcon class="h-4 w-4" />
                  <span>{{ post.location }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Ad Banner 1 - After Header -->
        <section v-if="adsEnabled" class="w-full py-6 bg-gray-light dark:bg-graphite-light">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="text-center mb-4">
              <span class="text-xs font-body text-graphite dark:text-gray-light uppercase tracking-wide">{{ $t('ads.advertisement') }}</span>
            </div>
            <AdSenseAd 
              :ad-slot-id="headerAdConfig?.id"
              :ad-format="headerAdConfig?.format"
              :ad-style="headerAdConfig?.style"
              container-class="max-w-4xl mx-auto"
              placeholder-class="banner"
              :lazy-load="headerAdConfig?.lazyLoad"
            />
          </div>
        </section>

        <!-- Contenido del post -->
        <div class="p-6 md:p-8">
          <!-- Información adicional -->
          <div v-if="post.difficulty" class="mb-6 p-4 bg-gray-light dark:bg-graphite-light rounded-lg">
            <h3 class="font-heading font-semibold mb-2 text-graphite dark:text-white">{{ $t('post.routeInfo') }}</h3>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-body">
              <div v-if="post.distance">
                <span class="text-graphite dark:text-gray-light">{{ $t('post.distance') }}:</span>
                <p class="font-medium text-graphite dark:text-white">{{ post.distance }}</p>
              </div>
              <div v-if="post.duration">
                <span class="text-graphite dark:text-gray-light">{{ $t('post.duration') }}:</span>
                <p class="font-medium text-graphite dark:text-white">{{ post.duration }}</p>
              </div>
              <div v-if="post.difficulty">
                <span class="text-graphite dark:text-gray-light">{{ $t('post.difficulty') }}:</span>
                <p class="font-medium" :class="getDifficultyColor(post.difficulty)">{{ post.difficulty }}</p>
              </div>
              <div v-if="post.location">
                <span class="text-graphite dark:text-gray-light">{{ $t('post.location') }}:</span>
                <p class="font-medium text-graphite dark:text-white">{{ post.location }}</p>
              </div>
            </div>
          </div>

          <!-- Contenido principal con anuncios integrados -->
          <div class="prose prose-lg dark:prose-invert max-w-none">
            <div v-html="processedContent"></div>
          </div>

          <!-- Acciones -->
          <div class="mt-8 pt-6 border-t border-gray-light dark:border-graphite-light">
            <div class="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <div class="flex gap-2">
                <button 
                  @click="sharePost"
                  class="px-4 py-2 bg-primary text-graphite rounded-md hover:bg-primary-dark transition-colors font-body"
                >
                  <ShareIcon class="h-4 w-4 inline mr-2" />
                  {{ $t('post.share') }}
                </button>
                <button 
                  v-if="post.category === 'routes'" 
                  @click="downloadGPX"
                  class="px-4 py-2 border border-gray-light dark:border-graphite-light text-graphite dark:text-gray-light rounded-md hover:bg-gray-light dark:hover:bg-graphite-light transition-colors font-body"
                >
                  <DownloadIcon class="h-4 w-4 inline mr-2" />
                  {{ $t('post.downloadGPX') }}
                </button>
              </div>
              <router-link 
                :to="post.slug === 'madridbcn2023' ? '/' : '/blog'"
                class="px-6 py-2 bg-gray-light dark:bg-graphite-light text-graphite dark:text-gray-light rounded-md hover:bg-gray-light/80 dark:hover:bg-graphite-light/80 transition-colors font-body"
              >
                {{ post.slug === 'madridbcn2023' ? $t('nav.home') : $t('blog.backToBlog') }}
              </router-link>
            </div>
          </div>
        </div>

        <!-- Ad Banner 2 - Before Footer -->
        <section v-if="adsEnabled" class="w-full py-6 bg-gray-light dark:bg-graphite-light">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="text-center mb-4">
              <span class="text-xs font-body text-graphite dark:text-gray-light uppercase tracking-wide">{{ $t('ads.advertisement') }}</span>
            </div>
            <AdSenseAd 
              :ad-slot-id="footerAdConfig?.id"
              :ad-format="footerAdConfig?.format"
              :ad-style="footerAdConfig?.style"
              container-class="max-w-4xl mx-auto"
              placeholder-class="banner"
              :lazy-load="footerAdConfig?.lazyLoad"
            />
          </div>
        </section>
      </div>

      <!-- Loading state -->
      <div v-else class="flex items-center justify-center min-h-screen">
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p class="font-body text-graphite dark:text-gray-light">{{ $t('blog.loading') }}</p>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="flex flex-col gap-2 sm:flex-row py-6 w-full shrink-0 items-center px-4 md:px-6 border-t border-gray-light dark:border-graphite-light bg-gray-light dark:bg-graphite">
      <p class="text-xs font-body text-graphite dark:text-gray-light">{{ $t('footer.copyright') }}</p>
      <nav class="sm:ml-auto flex gap-4 sm:gap-6">
        <router-link to="/privacy" class="text-xs font-body hover:underline underline-offset-4 text-graphite dark:text-gray-light">{{ $t('footer.privacy') }}</router-link>
        <router-link to="/terms" class="text-xs font-body hover:underline underline-offset-4 text-graphite dark:text-gray-light">{{ $t('footer.terms') }}</router-link>
        <router-link to="/contact" class="text-xs font-body hover:underline underline-offset-4 text-graphite dark:text-gray-light">{{ $t('footer.contact') }}</router-link>
      </nav>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { 
  Calendar as CalendarIcon, 
  Route as RouteIcon, 
  Clock as ClockIcon,
  MapPin as MapPinIcon,
  Share as ShareIcon,
  Download as DownloadIcon
} from 'lucide-vue-next'
import AdSenseAd from '@/components/AdSenseAd.vue'
import { getAdSlotConfig, isAdSenseEnabled } from '@/config/adsense'

const { t } = useI18n()

// Props
const route = useRoute()
const post = ref(null)

// Configuración de anuncios
const headerAdConfig = getAdSlotConfig('postHeader')
const inArticleAdConfig = getAdSlotConfig('postInArticle')
const footerAdConfig = getAdSlotConfig('postFooter')
const adsEnabled = isAdSenseEnabled()

// Datos de los posts (en un proyecto real, esto vendría de una API)
const posts = [
  // Eventos
  {
    id: 1,
    slug: 'madridbcn2023',
    category: 'events',
    title: 'Madrid Barcelona',
    excerpt: 'Evento de ultracycling en carretera de 700km con 8000m de desnivel. Una prueba de resistencia que conecta dos de las principales ciudades de España.',
    content: `
      <h2>2 de junio - Preparativos</h2>
      <p>A las 6AM salimos de casa dirección Barcelona Sants a coger el tren que me llevaría a Madrid. A las 08:10 sale puntual de Sants y llegamos a Madrid a las 10:40. Taxi y rumbo al Centro Deportivo Municipal Margot Moles.</p>
      <p>Sacamos bici y hacemos la ronda de acreditaciones y recogida de bolsa del corredor. Por ahora aunque los nervios controlados, se viene la parte que no dominamos... hay que volver a montar todos las piezas de la bicicleta que desmontamos para que entrara en su bolsa de viaje. Manillar, cambio trasero, pedales... poco a poco todo va en su sitio y después de un pequeño test lo doy por bueno...</p>
      <div class="my-6">
        <img src="/images/madbcn23/foto_0.png" alt="Preparativos Madrid Barcelona" class="w-full rounded-lg shadow-lg" />
      </div>
      <p>A las 12 empezamos el briefing y @ununibi y su equipo nos acaban de confirmar tramos del trayecto y dudas varias.</p>
      <p>Se acerca la hora de comer y nos dirigimos unos cuantos hasta un bar cercano recomendado por la organización donde ya hay más corredores.</p>
      <p>Panza llena toca intentar descansar antes de la salida, me tumbo pero no hay quien duerma, los nervios de lo que tiene que llegar no perdonan. Así que recojo el tracker lo instalo en las bolsas y acabo preparando toda la comida que me llevaré y empiezo a vestirme. Entrego mis bolsas y ale solo quedaba esperar.</p>
      <p>Poco antes de las 18h nos dirigimos a la pista de atletismo donde el sol aun golpea cuando daban agua desde bien pronto de la mañana... ¿nos salvariamos de esa segura agua? Era la pregunta que más se escuchaba.</p>
      <p>18:00 ya estamos los 88 participantes de esta edición. 58 en modalidad solo y 15 parejas. Por delante casi 700km, más de 7000m de desnivel positivo y 50h para completar el reto.</p>

      <h2>CP0 - CP1 (2 de junio 18:00)</h2>
      <div class="my-6">
        <img src="/images/madbcn23/foto_cp0.jpg" alt="Salida Madrid Barcelona" class="w-full rounded-lg shadow-lg" />
      </div>
      <p>Los participantes esperabamos expectantes a que dieran la salida y a la hora en punto por fin podíamos soltar los nervios y empezar a pedalear.</p>
      <p>Sorpresa! No llevamos ni media vuelta a la pista que empiezan a caer las primeras gotas de lluvia! Cosa que yo al menos agradecía dado el sol que nos estaba dando desde buena mañana.</p>
      <p>Los primeros KMs hasta salir de Madrid son neutralizados. Hasta el km50 más o menos esta permitido el rodar en grupo y lo aprovechamos.</p>
      <p>No obstante no llevamos ni 5min que empieza a diluviar... la gente empieza a pararse y ponerse los chubasqueros pero yo decido seguir sin él. La temperatura es agradable y decido sufrir esta lluvia y guardarlo como ropa de abrigo para más adelante y que esté seca.</p>
      <p>Y las malas noticias no paraban. Pues en estos primeros KMs debo realizar dos paradas de emergencia a revisar la dirección la cual no habia apretado correctamente.</p>
      <p>Vaciamos la mente de malos pensamientos y a remontar, me acoplo bien en los aerobars y a recuperar posiciones. Al cabo de 20min vuelvo a estar en el grupo inicial.</p>
      <p>La lluvia es fuerte pero corta pero el cuerpo ya esta empadado asi que decido subir un poco el ritmo a ver si recortamos distancia con grupos delanteros. Me suelto de mi grupeta y voy adelantando gente. Bien acoplado me siento agusto y veo como las distancias se recortan.</p>
      <p>La carretera cambia y dejamos atrás vías rápidas y empezamos a encadenar carreteras con muy poco por no decir, sin tránsito, y pasar por pequeños pueblos.</p>
      <p>La luz empieza a caer y va siendo hora de encender las luces justo al coronar el último puerto antes del CP1. Sensaciones increibles poder estar pedalenado por zonas tan tránquilas y bellas.</p>
      <p>Justo al iniciar el descenso a Cifuentes, donde esta nuevo CP1, empieza a llover con ganas otra vez, dificultando la bajada ya con noche cerrada. A lo lejos se ven las luces del pueblo y llegados al CP1 las voces de los habitantes que siendo viernes están en el bar con una animada conversación y sus cervezas.</p>
      <div class="my-6">
        <img src="/images/madbcn23/foto_cp1.png" alt="CP1 Cifuentes" class="w-full rounded-lg shadow-lg" />
      </div>

      <h2>CP1 - CP2 (2 junio 22:30 Cifuentes)</h2>
      <p>Sellar, abrigarse, preparar la comida para el siguiente tramo y salgo dirección el CP2 solo.</p>
      <p>Nada más salir del pueblo la cosa pica para arriba cojo un ritmo constante y encaro una carretera solitaria bajo un manto de estrellas y la luna llena. Parece que nos da una tregua la noche. Aún no hace mucho frío pero la lluvia previa me ha dejado empapado.</p>
      <p>El primer tramo de este sector es el más exigente de la carrera, acumulando en menos de 100km más de 1000m de desnivel y tocando cotas de casi 1400m de altitud los más altos de la prueba.</p>
      <p>Entre los dos puestos sobre el km185 pasamos por un pueblo y paro en la gasolinera, que tiene máquina de comida y bebidas y cargo un bidón con CocaCola. En esta pausa me adelantan 3 corredores a los cuales namás salir busco dar caza. Será en medio del siguiente puerto cuando por fin les cojo la rueda. Circular de noche me da una sensación de libertad y disfrute nunca vistos. Te obliga a agudizar los sentidos, cualquier error puede ser clave. Entre los corredores que alcanzo esta @kornelmartinger un joven polaco rookie como yo en este tipo de carreras. Y con el cual compartiria muchos más KMs más adelante.</p>
      <p>Finalmente llega la gran bajada al Cañón del río Mesa.</p>
      <p>Si bajo la luz de la luna ya es espectacular, de día esas paredes verticales de más de 100m que nos rodean deben de ser increibles.</p>
      <p>Una vez salimos del cañon solo nos queda remontar un poco hasta el pueblo de Nuévalos y llegar al CP2.</p>
      <div class="my-6">
        <img src="/images/madbcn23/foto_cp2.jpg" alt="CP2 Nuévalos" class="w-full rounded-lg shadow-lg" />
      </div>
      <div class="bg-gray-light dark:bg-graphite-light p-4 rounded-lg my-4">
        <p class="font-body text-graphite dark:text-white mb-0"><strong>Distancia:</strong> 117km | <strong>Tiempo:</strong> 4h30m</p>
      </div>

      <h2>CP2 - CP3 (3 junio 03:30)</h2>
      <p>Antes de salir del CP2, toca revisar la bolsa del manillar. Con el traqueteo se me ha soltado un poco las cintas y rozaban con la rueda delantera.</p>
      <p>Una vez todo en su sitio arranco dirección el punto de control 3 en Belchite.</p>
      <p>Se vienen las peores horas de la noche. Siempre me habian hablado que lo peor para el cuerpo es cuando esta apunto de salir el sol por la mañana, aguantar ese bajón es complicado y el tema sueño se hace duro. Hasta ahora ,no se si por la CocaCola o por lo nuevo de esta carrera, que no he tenido ninguna señal de sueño. A parte mi cuerpo esta bien entrenado para la distancia ya recorrida, pero cuidado que cuando llegue a Belchite superaré mi máximo de KMs...</p>
      <p>Vamos encadenando carreteras cada vez más pequeñas y estrechas y asumiendo estas cuotas.</p>
      <p>Llega la última bajada que nos acercara al pueblo de Daroca. Son las 5AM y mi cuerpo me pide dormir... pero mirando los tramos que recorro, lo empapado que está todo y el frío que hace, por debajo de los 7ºC, decido aminorar la marcha pero seguir pedaleando...</p>
      <p>Finalmente llego a Daroca y justo a sus puertas me pillan mi amigo polaco @kornelmartinger y @cicli_ettore. Ettore decide parar a buscar comida, yo la verdad que solo pensaba en un cafe bien calentito pero al no ver ningún bar abierto decidimos seguir adelante.</p>
      <p>Empieza a despuntar el sol por delante y encadenamos puertos con tramos infinitos de campos de conreo. Cuando de golpe la naturaleza nos brinda una imagen que ni a @kornelmartinger ni a mi se nos olvidará en mucho tiempo. Justo cuando los primeros rayos de sol nos dan en la cara, una familia de jabalíes, unas 10 unidades, entre adultos y javatos, se ponen a correr en paralelo a nuestro lado para cruzar la carretera unos metros más adelante.</p>
      <p>Con el sol, mi cuerpo ya tiene el calor que le hacia falta, asi que con eso y que ya hemos coronado el último puerto solo hay que dejar que la bici descienda hasta el CP3 no sin algún que otro repecho no amigable.</p>
      <div class="my-6">
        <img src="/images/madbcn23/foto_cp3.jpg" alt="CP3 Belchite" class="w-full rounded-lg shadow-lg" />
      </div>
      <div class="bg-gray-light dark:bg-graphite-light p-4 rounded-lg my-4">
        <p class="font-body text-graphite dark:text-white mb-0"><strong>Distancia:</strong> 123km | <strong>Desnivel:</strong> 1353m</p>
      </div>

      <h2>CP3 - CP4 (3 junio 09:15 AM)</h2>
      <p>El cuerpo empieza a estar bastante tocado, los ultimos KMs hasta Belchite han sido en completo silencio solo roto al ver nuestra llegada al CP. Sobretodo las rodillas piden ya una parada "larga".</p>
      <p>Asi que toca aprovechar el bar donde estamos y meternos en el cuerpo su primera comida real desde el almuerzo del viernes en Madrid.</p>
      <p>Bocadillo de tortilla, cocacola, cortado y un croisant de chocolate blanco. Y un merecido descanso de unos 30min.</p>
      <p>Se viene el tramo más duro en todos los sentidos... echo un ojo al radar de lluvia y parece que tendremos agua y/o nubes en los próximos kms asi que decido no alargar y aprovechar esta suerte. Los últimos años han llegado a superar los 40ºC en estre tramo.</p>
      <p>Cargo bien de agua y arranco por unas carreteras llanas de molinos de viento. Muy monótono y sin ninguna alegría para la mente pero al menos el cuerpo no tiene que superar desniveles. Caen 4 gotas pero nada más.</p>
      <p>Paso las últimas poblaciones de Escatron encandenando tramos de largas bajadas pero cuando la carretera se acerca a Caspe todo cambia...</p>
      <p>No hay ninguna nube, el sol empieza a pegar fuerte, carretera nacional con bastante tráfico y ningún sitio donde esconderse de ese sol que hace que la temperatura esté sobrepasando los 35ºC. Encadenar toboganes en rectas infinitas y para rematar 5km duros con un tramo final de 2km por encima del 10%.</p>
      <p>Mi cuerpo va al límite y por primera vez mi mente esta sufriendo, voy con lo justo de agua para llegar al CP4 tengo que controlar mis ganas de beber... Acabo coronando haciendo eses en la carretera, es la única manera que encuentro de hacer sufrir menos mis rodillas.</p>
      <p>Eso si coronar y larga bajada a Mequinenza. Vistas por fin disfrutonas del paisaje. He completado el tramo más duro y creo que lo ha sido más para mi cabeza que mis piernas... pero llego al CP con las rodillas al rojo vivo y sin un ápice de fuerza. Toca comer y por fin descansar...</p>
      <div class="my-6">
        <img src="/images/madbcn23/foto_cp4.jpg" alt="CP4 Mequinenza" class="w-full rounded-lg shadow-lg" />
      </div>
      <div class="bg-gray-light dark:bg-graphite-light p-4 rounded-lg my-4">
        <p class="font-body text-graphite dark:text-white mb-0"><strong>Distancia:</strong> 108km | <strong>Desnivel:</strong> 1069m</p>
      </div>

      <h2>CP4 - CP5 (3 Junio 18:00)</h2>
      <p>Comer un buen menú, una siesta recuperadora de 2h clavadas en un sofa que me salva la vida...</p>
      <p>Pero he perdido muchas plazas y no hay ningún corredor cerca. Toca hacerse hasta el CP5 en busca y captura de un grupo o tocara llegar solo a Barcelona.</p>
      <p>Antes de dormirme he visto en el radar de lluvia que se acercaba tormenta... cuando salgo y veo las nubes a lo lejos me llegan recuerdas de la anterior tarde en Madrid...</p>
      <p>Sin pensarlo más subo a la bici y pongo en foco en no pensar más que en dar pedales. Un primer tramo en ligera subida y viento de cara no es la mejor forma de empezar...</p>
      <p>Pasan los KMs y cuando por fin me acerco al tramo que reconocimos unas semanas atrás la dirección cambia y me da un pequeño respiro. Se va acercando la noche...</p>
      <p>Finalmente tengo que volver a encender las luces a pocos KMs de Guimerà. Que bella me pareció semanas atrás y que poco la disfruto ahora. Noche cerrada, frío y al salir de la población tramo de niebla espesa. La carretera aunque sin grandes desniveles siempre va picando para arriba y al coronar el puerto y antes de encarar la bajada al CP5 decido parar y abrigarme. No quiero coger frío en los 2km que me quedan hasta el bar y punto de control 5 que me detengan más de lo necesario.</p>
      <p>Despues de casi 6h llego al CP5. Barcelona cada vez esta más cerca, pero estos KMs solo me dan que pensar si podre llegar a destino sin compañia...</p>
      <div class="bg-gray-light dark:bg-graphite-light p-4 rounded-lg my-4">
        <p class="font-body text-graphite dark:text-white mb-0"><strong>Distancia:</strong> 112km | <strong>Desnivel:</strong> 1295m</p>
      </div>

      <h2>CP5 - Meta (3 junio 23:30)</h2>
      <p>Llego al CP5 con frío, pero con una idea clara. Recargar bebida, abrigarme bien y para Barcelona. En las afueras del bar, ninguna bici y hay mucho movimiento. Dejo cargando las baterías de luces y gps. Y entro al bar. Sorpresa! Me encuentro en una mesa acabando de cenar a @cicli_ettore , @annika_vossen , Ismael y Mario. Me centro en lo mio. Cafe solo para el cuerpo y un helado. Y cargo el bidón con dos CocaColas. Ya tengo suficiente energía para el cuerpo y aun me quedo algun pastelito en la bolsa.</p>
      <p>Nos abrigamos bien todos, parece que tendremos una noche fría y ahora vienen tramos de bajadas largas.</p>
      <p>Con más pena que gloria vamos coronando los pequeños repechos que nos quedan y dejamos que la bici corra en las bajadas. No tenemos fuerzas para más.</p>
      <p>Después del largo tramo de Gelida y su bajada hasta Martorell queda la parte más larga del viaje. Superar los polígonos y semáforos de las localidades cercanas a Barcelona, a nuestra meta.</p>
      <p>A parte de las pocas fuerzas que nos quedan para pedalear el ambiente tampoco mantenemos largas conversaciones. Mantenemos el grupo unido. Aquí no queda nadie atrás.</p>
      <p>Finalmente a las 04:09 del domingo 4 de junio y tras 34h y 9min cruzamos la meta y tocamos la campana de PEDALMA. ¡SOMOS FINISHERS!</p>
      <div class="my-6">
        <img src="/images/madbcn23/foto_finish.jpg" alt="Llegada a Barcelona - Madrid Barcelona 2023" class="w-full rounded-lg shadow-lg" />
      </div>

      <h2>Galería</h2>
      <p>Un resumen visual de esta increíble aventura de 700km desde Madrid hasta Barcelona.</p>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 my-6">
        <div class="overflow-hidden rounded-lg shadow-lg">
          <img src="/images/madbcn23/foto_0.png" alt="Preparativos Madrid Barcelona" class="w-full h-64 object-cover hover:scale-105 transition-transform duration-300" />
        </div>
        <div class="overflow-hidden rounded-lg shadow-lg">
          <img src="/images/madbcn23/foto_cp0.jpg" alt="Salida Madrid Barcelona" class="w-full h-64 object-cover hover:scale-105 transition-transform duration-300" />
        </div>
        <div class="overflow-hidden rounded-lg shadow-lg">
          <img src="/images/madbcn23/foto_cp1.png" alt="CP1 Cifuentes" class="w-full h-64 object-cover hover:scale-105 transition-transform duration-300" />
        </div>
        <div class="overflow-hidden rounded-lg shadow-lg">
          <img src="/images/madbcn23/foto_cp2.jpg" alt="CP2 Nuévalos" class="w-full h-64 object-cover hover:scale-105 transition-transform duration-300" />
        </div>
        <div class="overflow-hidden rounded-lg shadow-lg">
          <img src="/images/madbcn23/foto_cp3.jpg" alt="CP3 Belchite" class="w-full h-64 object-cover hover:scale-105 transition-transform duration-300" />
        </div>
        <div class="overflow-hidden rounded-lg shadow-lg">
          <img src="/images/madbcn23/foto_cp4.jpg" alt="CP4 Mequinenza" class="w-full h-64 object-cover hover:scale-105 transition-transform duration-300" />
        </div>
        <div class="overflow-hidden rounded-lg shadow-lg md:col-span-2 lg:col-span-3">
          <img src="/images/madbcn23/foto_finish.jpg" alt="Llegada a Barcelona - Madrid Barcelona 2023" class="w-full h-80 object-cover hover:scale-105 transition-transform duration-300" />
        </div>
      </div>
    `,
    image: '/images/madridbcn.jpg',
    date: '2023-06-02',
    distance: '700km',
    duration: '34h 9min',
    location: 'Madrid - Barcelona, España',
    difficulty: 'Extremo'
  },
  // Eventos
  {
    id: 2,
    slug: 'kromvojoj2025',
    category: 'events',
    title: 'Kromvojoj 2025: El Abandono que Enseñó Más que el Éxito',
    excerpt: '1500 kilómetros y 28.000 metros de desnivel positivo. Mi experiencia en Kromvojoj 2025, un evento de ultracycling que me enseñó que a veces el abandono es la mejor decisión cuando la mente y el cuerpo no están alineados.',
    content: `
      <h2>Kromvojoj 2025: El Reto</h2>
      <p>Kromvojoj es uno de los eventos de ultracycling más desafiantes de Europa. 1500 kilómetros de ruta con 28.000 metros de desnivel positivo acumulado, una prueba que pone a prueba no solo la resistencia física, sino también la fortaleza mental y la capacidad de adaptación ante imprevistos.</p>
      
      <p>El sábado 7 de junio de 2025, 100 ciclistas nos reunimos en Castellvell del Camp, en la provincia de Tarragona, para enfrentarnos a este desafío épico. La salida estaba programada para las 18:00 horas, y el ambiente era una mezcla de nervios, emoción y determinación.</p>
      
      <h3>Los Números del Desafío</h3>
      <ul>
        <li><strong>Distancia total:</strong> 1.500 kilómetros</li>
        <li><strong>Desnivel acumulado:</strong> 28.000 metros positivos</li>
        <li><strong>Participantes:</strong> 100 ciclistas</li>
        <li><strong>Punto de salida:</strong> Castellvell del Camp, Tarragona</li>
        <li><strong>Hora de salida:</strong> 18:00 horas (sábado 7 de junio)</li>
        <li><strong>Primer punto de control:</strong> La Creueta</li>
      </ul>
      
      <h2>La Preparación</h2>
      <p>La preparación para Kromvojoj había comenzado meses antes. Sabía que este evento sería diferente a cualquier otro que hubiera enfrentado. Con 28.000 metros de desnivel, cada kilómetro sería una batalla contra la gravedad.</p>
      
      <h3>Entrenamiento Específico</h3>
      <p>Mi preparación se centró en:</p>
      <ul>
        <li><strong>Volumen de entrenamiento:</strong> Aumenté progresivamente hasta alcanzar 20-25 horas semanales en las semanas pico</li>
        <li><strong>Entrenamientos de desnivel:</strong> Sesiones específicas en zonas montañosas para acostumbrar al cuerpo a subidas prolongadas</li>
        <li><strong>Resistencia mental:</strong> Sesiones de 12-16 horas consecutivas para simular las condiciones de carrera</li>
        <li><strong>Preparación del equipo:</strong> Testing exhaustivo de todos los componentes, especialmente frenos y transmisión</li>
      </ul>
      
      <h3>Estrategia de Carrera</h3>
      <p>Mi plan inicial era:</p>
      <ul>
        <li>Mantener un ritmo conservador los primeros 500km</li>
        <li>Gestionar el sueño con micro-siestas de 2-3 horas</li>
        <li>Nutrición constante: 300-400 calorías por hora</li>
        <li>Hidratación: beber cada 15-20 minutos</li>
        <li>Revisión preventiva del equipo en cada punto de control</li>
      </ul>
      
      <h2>La Salida y los Primeros Kilómetros</h2>
      <p>A las 18:00 horas en punto, los 100 participantes comenzamos nuestro viaje. El ambiente era eléctrico, con familiares y amigos animando desde la salida. El primer objetivo era alcanzar La Creueta, el primer punto de control.</p>
      
      <p>Los primeros kilómetros fueron de calentamiento, con el grupo compacto y un ritmo controlado. Sabía que era crucial no dejarse llevar por la emoción inicial y mantener un ritmo sostenible.</p>
      
      <h3>Hacia el Primer Control: La Creueta</h3>
      <p>La ruta hacia La Creueta ya comenzaba a mostrar el carácter montañoso de Kromvojoj. Las primeras subidas servían como aviso de lo que vendría después. Llegué al primer punto de control con buenas sensaciones, habiendo mantenido un ritmo constante y sin forzar.</p>
      
      <h2>La Noche: Ritmo y Desafíos</h2>
      <p>Durante las primeras horas nocturnas, mi ritmo fue bueno. La temperatura era agradable, la bici respondía bien, y mi cuerpo se sentía fuerte. Avancé con confianza, sabiendo que tenía por delante muchos kilómetros y desniveles.</p>
      
      <p>La noche en ultracycling tiene algo especial. El mundo se reduce a tu pequeño círculo de luz, los sonidos se amplifican, y cada pensamiento se vuelve más intenso. Fue durante estas horas cuando más disfruté de la experiencia, pedaleando bajo las estrellas con la sensación de libertad que solo el ciclismo de larga distancia puede ofrecer.</p>
      
      <h2>El Fallo Mecánico: El Punto de Inflexión</h2>
      <p>Sin embargo, como suele pasar en ultracycling, los imprevistos llegaron. Un fallo mecánico que inicialmente parecía menor comenzó a complicarse. Aunque intenté solucionarlo en ruta, el problema persistía y comenzó a afectar mi postura sobre la bicicleta.</p>
      
      <h3>Las Consecuencias del Fallo</h3>
      <p>El fallo mecánico me obligó a adoptar una postura compensatoria que, con el paso de los kilómetros, comenzó a causar problemas:</p>
      <ul>
        <li><strong>Dolor en los isquiotibiales:</strong> La postura forzada tensó excesivamente los músculos posteriores del muslo</li>
        <li><strong>Problemas en los rodillos:</strong> La posición anómala generó sobrecarga en las articulaciones</li>
        <li><strong>Fatiga mental:</strong> La preocupación constante por el problema mecánico comenzó a consumir energía mental</li>
      </ul>
      
      <p>Intenté continuar, ajustando mi posición constantemente, pero el dolor iba en aumento. Cada pedalada se volvía más incómoda, y mi capacidad de mantener un ritmo eficiente se veía comprometida.</p>
      
      <h2>El Abandono: Una Decisión Difícil pero Correcta</h2>
      <p>El domingo a las 14:00 horas, después de 357,51 kilómetros y 6.482 metros de desnivel positivo acumulado, tomé la decisión más difícil: abandonar en Puigcerdà.</p>
      
      <p>Fue una decisión que no tomé a la ligera. Había invertido meses de preparación, había llegado hasta allí con esfuerzo, y abandonar nunca es fácil. Sin embargo, sabía que continuar en esas condiciones no solo comprometería mi salud, sino que tampoco me permitiría disfrutar de la experiencia.</p>
      
      <h3>Por Qué Fue la Decisión Correcta</h3>
      <p>En retrospectiva, el abandono fue la mejor decisión que pude tomar:</p>
      <ul>
        <li><strong>Prevención de lesiones graves:</strong> Continuar habría agravado los problemas en isquios y rodillos, posiblemente causando lesiones de larga duración</li>
        <li><strong>Respeto por el cuerpo:</strong> El ultracycling es un deporte de resistencia, pero también requiere escuchar las señales del cuerpo</li>
        <li><strong>Experiencia de aprendizaje:</strong> Este abandono me enseñó más sobre preparación, gestión de problemas y toma de decisiones que muchos éxitos anteriores</li>
      </ul>
      
      <h2>Lecciones Aprendidas</h2>
      <p>Kromvojoj 2025, aunque terminó en abandono, fue una de las experiencias más valiosas de mi carrera en ultracycling. Me enseñó lecciones importantes:</p>
      
      <h3>1. La Preparación Mecánica es Crucial</h3>
      <p>Un fallo mecánico puede arruinar meses de preparación física. Es esencial:</p>
      <ul>
        <li>Revisar exhaustivamente todos los componentes antes de la carrera</li>
        <li>Tener conocimientos básicos de mecánica para solucionar problemas en ruta</li>
        <li>Llevar un kit de herramientas completo y saber usarlo</li>
        <li>Probar el equipo en condiciones similares a las de la carrera</li>
      </ul>
      
      <h3>2. La Mente es Tan Importante Como el Cuerpo</h3>
      <p>Mi cabeza no me ayudó a mantener las energías después del fallo mecánico. Esto me enseñó que:</p>
      <ul>
        <li>La preparación mental es tan importante como la física</li>
        <li>Es crucial tener estrategias para manejar la frustración y los imprevistos</li>
        <li>La capacidad de adaptación y resiliencia mental puede marcar la diferencia</li>
        <li>Saber cuándo parar es una muestra de inteligencia, no de debilidad</li>
      </ul>
      
      <h3>3. El Abandono No Es Fracaso</h3>
      <p>En ultracycling, el abandono es parte del juego. No todos los intentos terminan en éxito, y eso está bien. Lo importante es:</p>
      <ul>
        <li>Aprender de cada experiencia, exitosa o no</li>
        <li>Respetar el cuerpo y la salud por encima de la ambición</li>
        <li>Usar cada abandono como oportunidad de mejora</li>
        <li>Mantener la pasión y la motivación para futuros desafíos</li>
      </ul>
      
      <h3>4. La Postura y la Ergonomía Son Fundamentales</h3>
      <p>Un pequeño problema mecánico que afecta la postura puede tener consecuencias graves:</p>
      <ul>
        <li>Es esencial mantener una postura correcta en todo momento</li>
        <li>Los ajustes menores pueden prevenir problemas mayores</li>
        <li>La ergonomía del equipamiento es crucial en distancias largas</li>
        <li>Es mejor parar y solucionar un problema que continuar con una postura comprometida</li>
      </ul>
      
      <h2>Los Números Finales</h2>
      <div class="bg-gray-light dark:bg-graphite-light p-4 rounded-lg my-4">
        <p class="font-body text-graphite dark:text-white mb-0">
          <strong>Distancia completada:</strong> 357,51 km<br>
          <strong>Desnivel acumulado:</strong> 6.482 m<br>
          <strong>Tiempo en ruta:</strong> Aproximadamente 20 horas<br>
          <strong>Punto de abandono:</strong> Puigcerdà<br>
          <strong>Resultado:</strong> Abandono por fallo mecánico y lesiones derivadas
        </p>
      </div>
      
      <h2>Mirando Hacia el Futuro</h2>
      <p>Kromvojoj 2025 no será mi último intento. Esta experiencia me ha dado las herramientas y el conocimiento para prepararme mejor en el futuro. He aprendido que:</p>
      <ul>
        <li>La preparación debe ser holística: física, mental y técnica</li>
        <li>Los imprevistos son parte del juego, y la capacidad de adaptación es clave</li>
        <li>El abandono inteligente es mejor que continuar con riesgo de lesión grave</li>
        <li>Cada experiencia, exitosa o no, es un paso más en el camino del aprendizaje</li>
      </ul>
      
      <p>Volveré a Kromvojoj, pero esta vez con una preparación más completa, un conocimiento más profundo de mis límites, y una mentalidad más fuerte. Porque en ultracycling, como en la vida, no se trata solo de llegar a la meta, sino de aprender y crecer en cada kilómetro del camino.</p>
      
      <h2>Consejos para Futuros Participantes</h2>
      <p>Basándome en mi experiencia, estos son mis consejos para quienes se enfrenten a Kromvojoj o eventos similares:</p>
      
      <h3>Preparación Técnica</h3>
      <ul>
        <li>Revisa exhaustivamente tu bicicleta antes de la carrera</li>
        <li>Aprende mecánica básica: cambio de cubierta, ajuste de frenos, reparación de cadena</li>
        <li>Prueba tu equipo en condiciones similares a las de la carrera</li>
        <li>Lleva herramientas y repuestos esenciales</li>
      </ul>
      
      <h3>Preparación Física</h3>
      <ul>
        <li>Entrena específicamente para el desnivel: 28.000m no es broma</li>
        <li>Incluye sesiones largas (12+ horas) en tu preparación</li>
        <li>Practica la gestión del sueño y la fatiga</li>
        <li>Entrena en condiciones similares a las de la carrera</li>
      </ul>
      
      <h3>Preparación Mental</h3>
      <ul>
        <li>Desarrolla estrategias para manejar la frustración</li>
        <li>Practica la visualización y técnicas de relajación</li>
        <li>Ten un plan B, C y D para diferentes escenarios</li>
        <li>Recuerda que el abandono inteligente no es fracaso</li>
      </ul>
      
      <h3>Durante la Carrera</h3>
      <ul>
        <li>Escucha a tu cuerpo constantemente</li>
        <li>No ignores las señales de dolor o fatiga extrema</li>
        <li>Mantén una postura correcta en todo momento</li>
        <li>Soluciona los problemas mecánicos de inmediato, no los dejes para después</li>
        <li>Disfruta del proceso, no solo del resultado</li>
      </ul>
      
      <p>Kromvojoj es un desafío épico que merece respeto y preparación. Con la preparación adecuada y la mentalidad correcta, es un evento que puede cambiar tu perspectiva sobre el ultracycling y sobre ti mismo.</p>
    `,
    image: '/images/madridbcn.jpg',
    date: '2025-06-07',
    distance: '357,51km (de 1500km)',
    duration: '20 horas',
    location: 'Cataluña, España (Castellvell del Camp - Puigcerdà)',
    difficulty: 'Extrema'
  },
  // Viajes
  {
    id: 3,
    slug: 'costa-maresme-cadaques-puigcerda',
    category: 'journeys',
    title: 'Costa del Maresme - Cadaqués - Puigcerdà',
    excerpt: 'Una ruta espectacular de bikepacking que recorre la costa catalana desde el Maresme hasta Cadaqués, para luego adentrarse en los Pirineos hasta Puigcerdà. Una combinación perfecta de costa y montaña.',
    content: `
      <h2>De la Costa a los Pirineos</h2>
      <p>Esta ruta de bikepacking es una de las más variadas y bellas de Cataluña. Combina los paisajes costeros del Maresme y la Costa Brava con la majestuosidad de los Pirineos, creando una experiencia única que muestra la diversidad geográfica de la región.</p>
      
      <h3>Información General de la Ruta</h3>
      <ul>
        <li><strong>Distancia total:</strong> Aproximadamente 250-300km (dependiendo de la variante)</li>
        <li><strong>Desnivel acumulado:</strong> 4.500-5.500m</li>
        <li><strong>Dificultad:</strong> Media-Alta</li>
        <li><strong>Duración recomendada:</strong> 3-4 días</li>
        <li><strong>Mejor época:</strong> Primavera y otoño (evitar verano por el calor costero)</li>
        <li><strong>Tipo de bicicleta:</strong> Gravel o bicicleta de carretera con neumáticos anchos</li>
      </ul>
      
      <h2>Etapa 1: Costa del Maresme - Cadaqués</h2>
      <p>La primera etapa recorre la costa catalana, desde las playas del Maresme hasta el encantador pueblo de Cadaqués, conocido por su relación con Salvador Dalí y sus paisajes mediterráneos únicos.</p>
      
      <h3>Puntos de Interés</h3>
      <ul>
        <li><strong>Inicio:</strong> Poblaciones costeras del Maresme (Mataró, Calella, Pineda de Mar)</li>
        <li><strong>Blanes:</strong> Puerta de entrada a la Costa Brava</li>
        <li><strong>Tossa de Mar:</strong> Villa medieval con murallas y playas</li>
        <li><strong>Llançà:</strong> Última población antes de Cadaqués</li>
        <li><strong>Cadaqués:</strong> Pueblo pesquero con arquitectura blanca característica</li>
      </ul>
      
      <h3>Características de la Etapa</h3>
      <ul>
        <li><strong>Distancia:</strong> 120-140km</li>
        <li><strong>Desnivel:</strong> 1.500-2.000m</li>
        <li><strong>Terreno:</strong> Principalmente carreteras secundarias costeras</li>
        <li><strong>Dificultad:</strong> Media (subidas costeras pero no extremas)</li>
      </ul>
      
      <h2>Etapa 2: Cadaqués - Puigcerdà</h2>
      <p>La segunda etapa abandona la costa y se adentra en los Pirineos, subiendo desde el nivel del mar hasta los más de 1.200 metros de altitud de Puigcerdà. Esta es la etapa más exigente físicamente.</p>
      
      <h3>Puntos de Interés</h3>
      <ul>
        <li><strong>Cadaqués:</strong> Salida desde el pueblo</li>
        <li><strong>Roses:</strong> Bahía y zona de humedales</li>
        <li><strong>Figueres:</strong> Ciudad natal de Dalí, con el Teatro-Museo Dalí</li>
        <li><strong>Camprodon:</strong> Pueblo de montaña en el valle del Ter</li>
        <li><strong>Ripoll:</strong> Monasterio de Santa Maria de Ripoll</li>
        <li><strong>Puigcerdà:</strong> Capital de la Cerdanya, punto final</li>
      </ul>
      
      <h3>Características de la Etapa</h3>
      <ul>
        <li><strong>Distancia:</strong> 130-160km</li>
        <li><strong>Desnivel:</strong> 3.000-3.500m</li>
        <li><strong>Terreno:</strong> Carreteras de montaña con puertos significativos</li>
        <li><strong>Dificultad:</strong> Alta (subidas prolongadas y exigentes)</li>
      </ul>
      
      <h2>Equipo Recomendado</h2>
      <ul>
        <li><strong>Bicicleta:</strong> Gravel o bicicleta de carretera con neumáticos de 28-32mm</li>
        <li><strong>Alforjas:</strong> Sistema de carga para 2-3 días</li>
        <li><strong>Ropa:</strong> Capas para cambios de temperatura (costa cálida, montaña fría)</li>
        <li><strong>Hidratación:</strong> Mínimo 2 bidones, considerar sistema de hidratación adicional</li>
        <li><strong>Navegación:</strong> GPS con track descargado</li>
        <li><strong>Kit de reparación:</strong> Herramientas básicas, cámara de repuesto, parches</li>
      </ul>
      
      <h2>Puntos de Reabastecimiento</h2>
      <h3>Etapa 1 (Costa)</h3>
      <ul>
        <li><strong>Maresme:</strong> Múltiples opciones en poblaciones costeras</li>
        <li><strong>Blanes:</strong> Supermercados y restaurantes</li>
        <li><strong>Tossa de Mar:</strong> Servicios completos</li>
        <li><strong>Cadaqués:</strong> Restaurantes y tiendas (limitado en temporada baja)</li>
      </ul>
      
      <h3>Etapa 2 (Montaña)</h3>
      <ul>
        <li><strong>Figueres:</strong> Supermercados y servicios completos</li>
        <li><strong>Camprodon:</strong> Bares y tiendas básicas</li>
        <li><strong>Ripoll:</strong> Servicios completos</li>
        <li><strong>Puigcerdà:</strong> Todos los servicios disponibles</li>
      </ul>
      
      <h2>Consejos Importantes</h2>
      <ul>
        <li><strong>Meteorología:</strong> Consultar el tiempo antes de salir, especialmente en montaña</li>
        <li><strong>Hidratación:</strong> Beber constantemente, especialmente en la etapa costera</li>
        <li><strong>Ritmo:</strong> Mantener un ritmo sostenible, especialmente en la subida a Puigcerdà</li>
        <li><strong>Seguridad:</strong> Cuidado con el tráfico en carreteras costeras en temporada alta</li>
        <li><strong>Alojamiento:</strong> Reservar con antelación en Cadaqués (muy turístico)</li>
        <li><strong>Fotografía:</strong> Llevar cámara, los paisajes son espectaculares</li>
      </ul>
      
      <h2>Mejor Época para Realizarla</h2>
      <ul>
        <li><strong>Primavera (Abril-Mayo):</strong> Temperaturas agradables, menos turismo costero</li>
        <li><strong>Otoño (Septiembre-Octubre):</strong> Clima ideal, paisajes con colores otoñales</li>
        <li><strong>Evitar:</strong> Verano (calor extremo en costa, mucho tráfico) e invierno (frío y nieve en montaña)</li>
      </ul>
      
      <h2>Experiencia Personal</h2>
      <p>Esta ruta ofrece una experiencia única al combinar dos mundos completamente diferentes: la tranquilidad y belleza de la costa mediterránea con el desafío y la majestuosidad de los Pirineos. El contraste entre pedalear junto al mar y luego enfrentarse a las subidas de montaña hace de esta ruta una de mis favoritas en Cataluña.</p>
      
      <p>El momento más memorable es sin duda la transición: dejar atrás el azul del Mediterráneo y comenzar a ver cómo el paisaje cambia gradualmente hacia las montañas, sabiendo que te espera un desafío físico significativo pero también recompensas visuales increíbles.</p>
      
      <h2>Descarga del Track GPX</h2>
      <p>El track GPX completo de la ruta está disponible para descarga. Incluye waypoints de puntos de interés, reabastecimiento y alojamiento recomendado.</p>
    `,
    image: '/images/madridbcn.jpg',
    date: '2024-05-15',
    distance: '250-300km',
    duration: '3-4 días',
    location: 'Cataluña, España (Costa del Maresme - Cadaqués - Puigcerdà)',
    difficulty: 'Media-Alta'
  }
]

// Métodos
const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const getCategoryLabel = (category) => {
  const labels = {
    journeys: t('blog.journeys'),
    events: t('blog.events'),
    routes: t('blog.routes')
  }
  return labels[category] || category
}

const getCategoryBadgeClass = (category) => {
  const classes = {
    journeys: 'bg-primary',
    events: 'bg-primary',
    routes: 'bg-primary'
  }
  return classes[category] || 'bg-graphite'
}

const getDifficultyColor = (difficulty) => {
  const colors = {
    'Fácil': 'text-primary',
    'Moderada': 'text-primary',
    'Difícil': 'text-primary',
    'Extremo': 'text-primary'
  }
  return colors[difficulty] || 'text-graphite'
}

const sharePost = () => {
  if (navigator.share) {
    navigator.share({
      title: post.value.title,
      text: post.value.excerpt,
      url: window.location.href
    })
  } else {
    // Fallback para navegadores que no soportan Web Share API
    navigator.clipboard.writeText(window.location.href)
    alert('Enlace copiado al portapapeles')
  }
}

const downloadGPX = () => {
  // Simulación de descarga de archivo GPX
  const gpxContent = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Lluis Bikepacking">
  <metadata>
    <name>${post.value.title}</name>
    <desc>${post.value.excerpt}</desc>
  </metadata>
  <!-- Aquí irían las coordenadas de la ruta -->
</gpx>`
  
  const blob = new Blob([gpxContent], { type: 'application/gpx+xml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${post.value.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.gpx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Procesar contenido para insertar anuncios
const processedContent = computed(() => {
  if (!post.value || !adsEnabled) return post.value?.content || ''
  
  const content = post.value.content
  const paragraphs = content.split('</p>')
  let adCount = 0
  
  return paragraphs.map((paragraph, index) => {
    let result = paragraph + '</p>'
    
    // Insertar anuncio cada 2 párrafos (después del 2do, 4to, 6to, etc.)
    if (index > 0 && index % 2 === 0 && adCount < 2) {
      adCount++
      result += `
        <div class="my-8 text-center">
          <div class="mb-4">
            <span class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">${t('ads.advertisement')}</span>
          </div>
          <div class="ad-placeholder in-article" data-ad-slot="${inArticleAdConfig?.id}"></div>
        </div>
      `
    }
    
    return result
  }).join('')
})

// Función para hacer scroll al top
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

// Función para buscar un post por ID o slug
const findPost = (identifier) => {
  // Si es un número, buscar por ID
  const numericId = parseInt(identifier)
  if (!isNaN(numericId)) {
    return posts.find(p => p.id === numericId)
  }
  // Si no es un número, buscar por slug
  return posts.find(p => p.slug === identifier)
}

// Cargar el post al montar el componente
onMounted(() => {
  post.value = findPost(route.params.id)
  scrollToTop()
})

// Hacer scroll al top cuando cambie la ruta (navegación entre posts)
watch(() => route.params.id, (newId) => {
  post.value = findPost(newId)
  scrollToTop()
})
</script>

<style scoped>
/* Estilos específicos para el componente */
.prose {
  max-width: none;
}

.prose h2 {
  @apply text-2xl font-heading font-bold text-graphite dark:text-white mt-8 mb-4;
}

.prose h3 {
  @apply text-xl font-heading font-semibold text-graphite dark:text-white mt-6 mb-3;
}

.prose p {
  @apply font-body text-graphite dark:text-gray-light mb-4 leading-relaxed;
}

.prose ul {
  @apply list-disc list-inside mb-4 space-y-2;
}

.prose li {
  @apply font-body text-graphite dark:text-gray-light;
}

.prose strong {
  @apply font-body font-semibold text-graphite dark:text-white;
}

.prose div {
  @apply my-4;
}

.prose img {
  @apply w-full rounded-lg shadow-lg my-6;
  max-width: 100%;
  height: auto;
}
</style>
  